<template>
                <div v-if="currentView === 'groups'">
                    <div class="page-header"><div><div class="page-title">群組管理</div><div class="page-subtitle">建立郵件分發群組與成員設定</div></div></div>
                    <div class="card p-6 mb-6">
                        <h3 class="card-title mb-3">新增群組</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label class="block text-sm mb-1">群組名稱 (例如 sales)</label>
                                <input v-model="newGroup.name" placeholder="sales" class="input">
                            </div>
                            <div>
                                <label class="block text-sm mb-1">所屬網域</label>
                                <select v-model="newGroup.domain_id" class="input">
                                    <option v-for="(d, idx) in safeArr(domains)" :key="keyById(d, 'domain-opt4', idx)" :value="d.id">{{ d.name }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="mt-4">
                            <label class="block text-sm mb-2">選擇成員</label>
                            <div class="border rounded-lg h-40 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-2" style="background:var(--surface);border-color:var(--border);">
                                <div v-for="(user, idx) in safeArr(users)" :key="keyById(user, 'user2', idx)" class="flex items-center">
                                    <input type="checkbox" :value="user.email" v-model="newGroup.members" class="mr-2">
                                    <span class="text-sm truncate" :title="user.email">{{ user.email }}</span>
                                </div>
                            </div>
                        </div>
                        <button @click="addGroup"
                            class="btn btn-success mt-4">建立群組</button>
                    </div>

                    <div class="card p-6">
                        <h3 class="card-title mb-4">群組列表</h3>
                        <table class="data-table">
                            <thead>
                                <tr class="border-b">
                                    <th class="p-2">群組信箱</th>
                                    <th class="p-2">成員</th>
                                    <th class="p-2">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="group in groups" :key="group.email" class="border-b">
                                    <td class="p-2 font-mono text-sm">{{ group.email }}</td>
                                    <td class="p-2">
                                        <div class="flex flex-wrap gap-1">
                                            <span v-for="member in group.members"
                                                class="text-xs px-2 py-1 rounded" style="background:var(--gray-200);color:var(--gray-700);">{{ member }}</span>
                                        </div>
                                    </td>
                                    <td class="p-2">
                                        <button @click="startEditGroup(group)"
                                            class="btn btn-ghost btn-xs">編輯</button>
                                        <button @click="deleteGroup(group.email)"
                                            class="btn btn-ghost btn-xs" style="color:var(--color-danger)">刪除</button>
                                    </td>
                                </tr>
                                <tr v-if="groups.length === 0">
                                    <td colspan="3" class="p-4 text-center" style="color:var(--gray-500);">尚無群組。</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Edit Group Modal -->
                <div v-if="editingGroup"
                    class="fixed inset-0 flex items-center justify-center z-50" style="background:rgba(0,0,0,0.5);">
                    <div class="card p-6 w-[600px] max-h-[90vh] overflow-y-auto">
                        <h3 class="card-title mb-4">編輯群組: {{ editingGroup.email }}</h3>
                        <div class="space-y-4">
                            <div>
                                <label class="block text-sm mb-2">成員</label>
                                <div class="border rounded-lg h-60 overflow-y-auto grid grid-cols-2 gap-2" style="background:var(--surface);border-color:var(--border);">
                                    <div v-for="(user, idx) in safeArr(users)" :key="keyById(user, 'user3', idx)" class="flex items-center">
                                        <input type="checkbox" :value="user.email" v-model="editingGroup.members"
                                            class="mr-2">
                                        <span class="text-sm truncate" :title="user.email">{{ user.email }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="flex justify-end gap-2">
                                <button @click="editingGroup = null" class="px-4 py-2 border rounded">取消</button>
                                <button @click="updateGroup"
                                    class="btn btn-primary">儲存變更</button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Server: Mail Service -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  newGroup, editingGroup,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup, addGroup, startEditGroup, updateGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
} = store
</script>

